import {useEffect, useRef, useState} from "react";
import type React from "react";

type HoldProgressOptions = {
    durationMs: number;
    onStart?: () => void;
    onComplete: () => void;
};

export function useHoldProgress(options: HoldProgressOptions) {
    const [progress, setProgress] = useState(0);
    const [isHolding, setIsHolding] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    }, []);

    const clearIntervalIfNeeded = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    };

    const stopProgress = () => {
        clearIntervalIfNeeded();
        setIsHolding(false);
        setProgress(0);
    };

    const finishProgress = () => {
        clearIntervalIfNeeded();
        setIsHolding(false);
        setProgress(100);
        options.onComplete();
        setTimeout(() => setProgress(0), 180);
    };

    const startProgress = (event: React.MouseEvent<Element>) => {
        if (event.button !== 0) {
            return;
        }
        setProgress(0);
        setIsHolding(true);
        options.onStart?.();
        const stepMs = options.durationMs / 10;
        intervalRef.current = setInterval(() => {
            setProgress((prev) => {
                const nextProgress = prev + 10;
                if (nextProgress >= 100) {
                    finishProgress();
                    return 100;
                }
                return nextProgress;
            });
        }, stepMs);
    };

    const handleMouseUp = () => stopProgress();
    const handleMouseLeave = () => stopProgress();

    return {
        progress,
        isHolding,
        eventHandlers: {
            onMouseDown: startProgress,
            onMouseUp: handleMouseUp,
            onMouseLeave: handleMouseLeave,
        },
    } as const;
}
